import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form } from "react-final-form";
import { Dialog } from "@syos-music/common";
import { TextField, SelectField } from "@syos-music/form";

import { Alert, Button, Grid, MenuItem } from "@mui/material";
import { LoadingButton } from "@mui/lab";

const MUTATION = gql`
  mutation (
    $orderId: ID!
    $paymentProvider: PaymentProvider!
    $paymentId: String!
  ) {
    CreateOrderInvoice(
      orderId: $orderId
      paymentProvider: $paymentProvider
      paymentId: $paymentId
    ) {
      order {
        id
        paymentProvider
        paymentId
        paymentDate

        invoice {
          id
          createdAt
          reference
          file {
            id
            url
          }

          creditNotes {
            id
            createdAt
            reference
            file {
              id
              url
            }
          }
        }
      }
    }
  }
`;

function CreateOrderInvoice({ order, children }) {
  const [isOpen, setIsOpen] = React.useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const invalidAddresses =
    !order.shippingAddress?.countryId || !order.billingAddress?.countryId;
  const invalidCart = !order.cart.length;

  const [createOrderInvoice] = useMutation(MUTATION);

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog open={isOpen} onClose={close}>
        <Form
          onSubmit={(values) =>
            createOrderInvoice({
              variables: {
                orderId: order.id,
                ...values,
              },
            })
              .then(() => close())
              .catch(() => true)
          }
          initialValues={
            process.env.NODE_ENV === "development"
              ? {
                  paymentProvider: "STRIPE",
                  paymentId: "TEST",
                }
              : {}
          }
        >
          {({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <Dialog.Title color="primary">Créer une facture</Dialog.Title>

              <Dialog.Content>
                <Grid container>
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      Cette commande sera considérée comme réglée. Le client ne
                      sera plus en mesure de la payer sur le site.
                    </Alert>
                  </Grid>

                  {invalidAddresses && (
                    <Grid item xs={12}>
                      <Alert severity="error">
                        Les adresses de livraison et/ou facturation sont
                        invalides !
                      </Alert>
                    </Grid>
                  )}

                  {invalidCart && (
                    <Grid item xs={12}>
                      <Alert severity="error">Le panier est vide !</Alert>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name="paymentProvider"
                      required
                      label="Moyen de paiement"
                    >
                      <MenuItem value="NONE">Aucun</MenuItem>
                      <MenuItem value="STRIPE">Stripe</MenuItem>
                      <MenuItem value="PAYPAL">Paypal</MenuItem>
                      <MenuItem value="AMAZON">Amazon</MenuItem>
                      <MenuItem value="SUMUP">SumUp</MenuItem>
                      <MenuItem value="TRANSFER">Virement</MenuItem>
                      <MenuItem value="CHECK">Chèque</MenuItem>
                      <MenuItem value="CASH">Liquide</MenuItem>
                    </SelectField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="paymentId"
                      required
                      label="Identifiant de paiement"
                    />
                  </Grid>
                </Grid>
              </Dialog.Content>

              <Dialog.Actions>
                <Button variant="text" onClick={close}>
                  Annuler
                </Button>

                <LoadingButton
                  type="submit"
                  loading={submitting}
                  disabled={invalidAddresses || invalidCart}
                >
                  Créer
                </LoadingButton>
              </Dialog.Actions>
            </form>
          )}
        </Form>
      </Dialog>
    </>
  );
}

CreateOrderInvoice.fragments = {
  order: gql`
    fragment Billing_CreateOrderInvoice_order on Order {
      id

      user {
        id
        profile {
          locale
        }
      }

      cart {
        id
      }

      shippingAddress {
        countryId
      }
      billingAddress {
        countryId
      }
    }
  `,
};

export default CreateOrderInvoice;
